import { type ReactNode, useState } from 'react';
import { t } from '@/lib/i18n';
import { Button, Dialog, Field, Input, Stack } from '@/ui';
import { ActionError } from './ActionError';

/**
 * Confirmation for destructive and high-risk actions: says what will happen, optionally asks
 * for a reason (kept in the audit trail), shows the server's refusal in place, and stays
 * open until the action succeeded.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  tone = 'danger',
  reason,
  typeToConfirm,
  blocked: notReady = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  /** Ask for a reason; `required` blocks confirming without one. */
  reason?: { label: string; required?: boolean };
  /** For the most dangerous actions: the user types this word to confirm. */
  typeToConfirm?: string;
  /** Not ready to confirm yet (e.g. a choice in `children` is still missing). */
  blocked?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const [text, setText] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const blocked = notReady || (reason?.required && !text.trim()) || (typeToConfirm !== undefined && typed.trim() !== typeToConfirm);

  const change = (o: boolean) => {
    if (busy) return;
    if (!o) {
      setText('');
      setTyped('');
      setError(null);
    }
    onOpenChange(o);
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(text.trim());
      setBusy(false);
      change(false);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={change}
      size="sm"
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={() => change(false)} disabled={busy}>
            {t('Cancel')}
          </Button>
          <Button variant={tone} loading={busy} disabled={blocked} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap={4}>
        {children}
        {reason && (
          <Field label={reason.label} required={reason.required}>
            {(f) => <Input {...f} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} />}
          </Field>
        )}
        {typeToConfirm !== undefined && (
          <Field label={t('Type {word} to confirm', { word: typeToConfirm })}>
            {(f) => <Input {...f} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />}
          </Field>
        )}
        <ActionError error={error} />
      </Stack>
    </Dialog>
  );
}
